/**
 * CepInput - Campo de CEP com busca automática de endereço via ViaCEP
 *
 * IMPORTANTE: Este componente DEVE ser usado sempre que uma tela de add/edit
 * tiver campo de CEP. Ele busca automaticamente o endereço ao preencher o CEP.
 *
 * FUNCIONALIDADE AUTOMÁTICA:
 * Quando o CEP é preenchido, o componente automaticamente preenche os campos
 * do formulário usando a função onFormChange (se fornecida). Os campos padrão são:
 * - endereco: logradouro retornado pelo ViaCEP
 * - bairro: bairro retornado pelo ViaCEP
 * - cidade: busca automática na lista de cidades (se cidadesLookup fornecido)
 *
 * @param {string} value - Valor do CEP
 * @param {function} onChange - Callback chamado quando o CEP muda (recebe o CEP formatado)
 * @param {function} onFormChange - Callback do formulário para alterar outros campos: (field, value) => void
 * @param {function} onAddressFound - Callback adicional chamado quando o endereço é encontrado (opcional)
 * @param {Array} cidadesLookup - Lista de cidades para busca automática [{id, cidade, estado: {uf}}]
 * @param {Object} fieldMapping - Mapeamento customizado dos campos (opcional)
 *   Default: { endereco: 'endereco', bairro: 'bairro', cidade: 'cidade', estado: 'estado' }
 * @param {string} placeholder - Placeholder do input (default: "00000-000")
 * @param {boolean} disabled - Desabilita o input
 * @param {object} style - Estilos adicionais para o input
 * @param {string} className - Classes CSS adicionais
 *
 * @example
 * // Uso com preenchimento automático (FormFieldsSection passa onFormChange automaticamente)
 * <CepInput
 *   value={formData.cep}
 *   onChange={(cep) => onChange('cep', cep)}
 *   onFormChange={onChange}
 *   cidadesLookup={cidades}
 * />
 *
 * @example
 * // Uso com mapeamento customizado de campos
 * <CepInput
 *   value={formData.cep}
 *   onChange={(cep) => onChange('cep', cep)}
 *   onFormChange={onChange}
 *   cidadesLookup={cidades}
 *   fieldMapping={{ endereco: 'logradouro', bairro: 'bairro', cidade: 'cidade_id' }}
 * />
 */

import React, { useState, type CSSProperties } from 'react';

type ViaCepResponse = {
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
  complemento: string;
  ibge: string;
};

type CidadeLookup = {
  id: string;
  cidade?: string | null;
  cod?: string | null;
  estado_id?: string | null;
  estado?: { uf?: string | null } | null;
};

type FieldMapping = {
  endereco?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
};

type OnFormChange = (field: string, value: unknown) => void;

interface CepInputProps {
  value?: string;
  onChange?: (cep: string) => void;
  onFormChange?: OnFormChange;
  onAddressFound?: (endereco: ViaCepResponse) => void;
  cidadesLookup?: CidadeLookup[];
  fieldMapping?: FieldMapping;
  focusAfterFill?: string | null;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
  className?: string;
}

/**
 * Busca endereço pelo CEP usando a API do ViaCEP
 * @param {string} cep - CEP (apenas números, 8 dígitos)
 * @returns {Promise<object|null>} Dados do endereço ou null se não encontrado
 */
export async function buscarEnderecoPorCep(cep: string): Promise<ViaCepResponse | null> {
  const cepLimpo = cep.replace(/\D/g, '');

  if (cepLimpo.length !== 8) {
    return null;
  }

  try {
    const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
    const data = await response.json();

    if (data.erro) {
      return null;
    }

    return {
      logradouro: data.logradouro || '',
      bairro: data.bairro || '',
      localidade: data.localidade || '',
      uf: data.uf || '',
      complemento: data.complemento || '',
      ibge: data.ibge || ''
    };
  } catch (err) {
    console.error('Erro ao buscar CEP:', err);
    return null;
  }
}

/**
 * Formata o CEP para exibição (00000-000)
 * @param {string} cep - CEP (pode conter formatação)
 * @returns {string} CEP formatado
 */
export function formatarCep(cep: string): string {
  const cepLimpo = cep.replace(/\D/g, '');

  if (cepLimpo.length <= 5) {
    return cepLimpo;
  }

  return `${cepLimpo.slice(0, 5)}-${cepLimpo.slice(5, 8)}`;
}

// Mapeamento padrão de campos do formulário
const DEFAULT_FIELD_MAPPING: Required<FieldMapping> = {
  endereco: 'endereco',
  bairro: 'bairro',
  cidade: 'cidade',
  estado: 'estado'
};

export default function CepInput({
  value = '',
  onChange,
  onFormChange,
  onAddressFound,
  cidadesLookup = [],
  fieldMapping = {},
  focusAfterFill = null, // Nome do campo para focar após preenchimento automático (ex: 'numero')
  placeholder = '00000-000',
  disabled = false,
  style = {},
  className = ''
}: CepInputProps): React.ReactElement {
  const [loading, setLoading] = useState<boolean>(false);

  // Merge do mapeamento customizado com o padrão
  const mapping = { ...DEFAULT_FIELD_MAPPING, ...fieldMapping };

  // Função SÍNCRONA para tratar a digitação (não pode ser async!)
  // Isso garante que o onChange seja chamado no mesmo tick do evento
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value;
    const cepLimpo = inputValue.replace(/\D/g, '');

    // Limita a 8 dígitos
    if (cepLimpo.length > 8) return;

    // Formata o CEP
    const cepFormatado = formatarCep(cepLimpo);

    // Notifica a mudança SINCRONAMENTE
    onChange?.(cepFormatado);

    // Busca endereço quando CEP tem 8 dígitos (ASYNC separado)
    if (cepLimpo.length === 8) {
      fetchAndFillAddress(cepLimpo);
    }
  };

  // Função ASSÍNCRONA separada para buscar endereço (não bloqueia digitação)
  const fetchAndFillAddress = async (cepLimpo: string): Promise<void> => {
    setLoading(true);
    const endereco = await buscarEnderecoPorCep(cepLimpo);
    setLoading(false);

    if (endereco) {
      // Preenchimento automático dos campos do formulário
      if (onFormChange) {
        // Preenche endereço (logradouro)
        if (endereco.logradouro && mapping.endereco) {
          onFormChange(mapping.endereco, endereco.logradouro);
        }

        // Preenche bairro
        if (endereco.bairro && mapping.bairro) {
          onFormChange(mapping.bairro, endereco.bairro);
        }

        // Busca e preenche cidade automaticamente
        // Primeiro tenta pelo código IBGE (mais preciso), depois pelo nome + UF
        if (mapping.cidade && cidadesLookup.length > 0) {
          let cidadeEncontrada: CidadeLookup | undefined;

          // Busca por código IBGE (campo 'cod' em cidade)
          if (endereco.ibge) {
            cidadeEncontrada = cidadesLookup.find(c => c.cod === endereco.ibge);
          }

          // Fallback: busca por nome + UF
          if (!cidadeEncontrada && endereco.localidade) {
            cidadeEncontrada = cidadesLookup.find(c =>
              c.cidade?.toLowerCase() === endereco.localidade.toLowerCase() &&
              c.estado?.uf === endereco.uf
            );
          }

          if (cidadeEncontrada) {
            onFormChange(mapping.cidade, cidadeEncontrada.id);
            // Também preenche o estado (para atualizar o select de estado)
            if (mapping.estado && cidadeEncontrada.estado_id) {
              onFormChange(mapping.estado, cidadeEncontrada.estado_id);
            }
          }
        }
      }

      // Callback adicional (para casos especiais)
      if (onAddressFound) {
        onAddressFound(endereco);
      }

      // Focar no próximo campo após preenchimento (ex: 'numero')
      if (focusAfterFill) {
        // Pequeno delay para garantir que o React atualizou o DOM
        setTimeout(() => {
          // Buscar input pelo name ou data-field attribute
          const nextInput = document.querySelector(
            `input[name="${focusAfterFill}"], ` +
            `input[data-field="${focusAfterFill}"], ` +
            `textarea[name="${focusAfterFill}"], ` +
            `textarea[data-field="${focusAfterFill}"]`
          );
          if (nextInput instanceof HTMLInputElement || nextInput instanceof HTMLTextAreaElement) {
            nextInput.focus();
          }
        }, 100);
      }
    }
  };

  const defaultStyle: CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--panel-border)',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.15s ease',
    ...style
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled || loading}
        className={className}
        style={defaultStyle}
      />
      {loading && (
        <div
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '16px',
            height: '16px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid var(--color-primary, #154680)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
        />
      )}
      <style>{`
        @keyframes spin {
          to { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
